import {
  AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params } from '@angular/router';
import { Store } from '@ngxs/store';
import { format } from 'date-fns';
import {
  BehaviorSubject, fromEvent, Observable, Subscription,
  timer
} from 'rxjs';
import {
  debounceTime, first, map, mergeMap, withLatestFrom
} from 'rxjs/operators';
import { LaboratoryBasicInfo } from 'src/app/services/dtos/laboratoryBasicInfo';
import { TaskSearchForm } from 'src/app/services/dtos/taskSearchForm';
import { DateTimeRangeFilterComponent } from 'src/app/shared/components/dateTime-range-filter/dateTime-range-filter.component';
import { InputFilterComponent } from 'src/app/shared/components/input-filter/input-filter.component';
import { InputOutputFilterComponent } from 'src/app/shared/components/input-output-filter/input-output-filter.component';
import { DateTimeRangeFilter } from 'src/app/shared/components/local-types/DateTimeRangeFilter';
import { SearchSelectMultipleComponent } from 'src/app/shared/components/search-select-multiple/search-select-multiple.component';
import { StatusFilterComponent } from 'src/app/shared/components/status-filter/status-filter.component';
import { CustomersState } from 'src/app/states/customers-state/customers.state';
import { LaboratoriesState } from 'src/app/states/laboratories-state/laboratories.state';
import { FetchAllPluginsBasicInfo } from 'src/app/states/plugins-state/plugins.actions';
import { PluginsState } from 'src/app/states/plugins-state/plugins.state';
import { TasksState } from 'src/app/states/tasks-state/tasks.state';
import { CustomerBasicInfo } from '../../../../services/dtos/customerBasicInfo';
import { RightsService } from '../../../../services/rights.service';
import { InputOutputFilter } from '../../../../shared/components/local-types/InputOutputFilter';
import { StatusFilter } from '../../../../shared/components/local-types/StatusFilter';
import { FetchCurrentUserAllCustomersBasicInfo } from '../../../../states/customers-state/customers.actions';
import { FetchCurrentUserLaboratoriesBasicInfo } from '../../../../states/laboratories-state/laboratories.actions';
import { StatusIconMapElement } from '../local-types/StatusIconMapElement';
import { TaskFilter } from '../local-types/TaskFilter';
import { TasksDateRangeFilterComponent } from '../tasks-date-range-filter/tasks-date-range-filter.component';
import { TaskBasicInfo } from './../../../../services/dtos/taskBasicInfo';
import { MessageState } from './../../../../services/enums/messageStates.enum';
import { ProcessFlow } from './../../../../services/enums/processFlow.enum';
import { TasksRefreshMode } from '../../../../services/enums/tasksRefreshMode.enum';
import { ProcessType } from './../../../../services/enums/processType.enum';
import {
  FetchTasksBasicInfo,
  SetCurrentTaskId
} from './../../../../states/tasks-state/tasks.actions';
import { ToastrService } from 'ngx-toastr';
import { Plugin } from '../../../../services/dtos/plugin';

@Component({
  selector: 'app-tasks-list',
  templateUrl: './tasks-list.component.html',
  styleUrls: ['./tasks-list.component.scss'],
})
export class TasksListComponent implements OnInit, AfterViewInit, OnDestroy {
  
  @ViewChild(TasksDateRangeFilterComponent)
  public dateRange!: TasksDateRangeFilterComponent;
  @ViewChild(DateTimeRangeFilterComponent)
  public dateFilter!: DateTimeRangeFilterComponent;
  @ViewChild(StatusFilterComponent) public statusFilter!: StatusFilterComponent;
  @ViewChild(InputOutputFilterComponent)
  public flowFilter!: InputFilterComponent;
  @ViewChild(SearchSelectMultipleComponent)
  public customerFilter!: SearchSelectMultipleComponent;
  @ViewChild(MatSort, { static: true })
  public sort!: MatSort;

  public arePluginsLoadedOrLoading$: Observable<boolean> = inject(Store).select(PluginsState.arePluginsLoadedOrLoading);
  public laboratories$: Observable<LaboratoryBasicInfo[]> = inject(Store).select(LaboratoriesState.getCurrentUserLaboratoriesBasicInfo);
  public customers$: Observable<CustomerBasicInfo[]> = inject(Store).select(CustomersState.getCurrentUserCustomersBasicInfo);
  public plugins$: Observable<Plugin[]> = inject(Store).select(PluginsState.getAllPlugins);
  public tasks$: Observable<TaskBasicInfo[]> = inject(Store).select(TasksState.getTasksBasicInfo);
  public selectedTaskId$: Observable<string> = inject(Store).select(TasksState.getSelectedTaskId);

  @ViewChild(MatPaginator) private paginator!: MatPaginator;
  @ViewChild('searchInput', { static: true }) private searchInput!: ElementRef;
  @ViewChild('customersSearch', { static: true })
  private customersSearch!: ElementRef;
  @ViewChild('laboratorySearch', { static: true })
  private laboratorySearch!: ElementRef;

  public customersDictionary!: CustomerBasicInfo[];
  public filteredCustomersDictionary!: CustomerBasicInfo[];
  public laboratoriesDictionary!: LaboratoryBasicInfo[];
  public filteredLaboratoriesDictionary!: LaboratoryBasicInfo[];
  public tasksRefreshMode = TasksRefreshMode;

  public taskStatusIconMap: StatusIconMapElement[] = [
    new StatusIconMapElement(
      ProcessType[ProcessType.input],
      MessageState[MessageState.inProgress],
      'input_in_progress',
      'started'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.input],
      MessageState[MessageState.readyForInput],
      'input_in_progress',
      'started'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.input],
      MessageState[MessageState.done],
      'input_done',
      'started'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.input],
      MessageState[MessageState.faulted],
      'faulted',
      'error'
    ),
    // eslint-disable-next-line max-len
    new StatusIconMapElement(
      ProcessType[ProcessType.transform],
      MessageState[MessageState.inProgress],
      'transform_in_progress',
      'in progress'
    ),
    // eslint-disable-next-line max-len
    new StatusIconMapElement(
      ProcessType[ProcessType.transform],
      MessageState[MessageState.readyToTransform],
      'transform_in_progress',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.transform],
      MessageState[MessageState.readyToMap],
      'transform_done',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.transform],
      MessageState[MessageState.done],
      'transform_done',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.transform],
      MessageState[MessageState.faulted],
      'faulted',
      'error'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.map],
      MessageState[MessageState.inProgress],
      'map_in_progress',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.map],
      MessageState[MessageState.readyToMap],
      'map_in_progress',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.map],
      MessageState[MessageState.readyForOutput],
      'map_done',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.map],
      MessageState[MessageState.done],
      'map_done',
      'in progress'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.map],
      MessageState[MessageState.faulted],
      'faulted',
      'error'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.output],
      MessageState[MessageState.inProgress],
      'output_in_progress',
      'in progress'
    ),
    // eslint-disable-next-line max-len
    new StatusIconMapElement(
      ProcessType[ProcessType.output],
      MessageState[MessageState.readyForOutput],
      'output_in_progress',
      'in progress'
    ),
    // eslint-disable-next-line max-len
    new StatusIconMapElement(
      ProcessType[ProcessType.output],
      MessageState[MessageState.readyForExternalFetch],
      'output_fetchable',
      'fetchable'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.output],
      MessageState[MessageState.done],
      'output_done',
      'done'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.output],
      MessageState[MessageState.faulted],
      'faulted',
      'error'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.done],
      MessageState[MessageState.done],
      'output_done',
      'done'
    ),
    new StatusIconMapElement(
      ProcessType[ProcessType.done],
      MessageState[MessageState.faulted],
      'faulted',
      'error'
    ),
  ];

  // Make enums available in HTML template:
  public processFlow = ProcessFlow;
  public processType = ProcessType;
  public messageState = MessageState;
  public advancedSearchOpened = false;
  public advancedSearchEnabled = false;
  public periodChangedFlag = false;
  public lastSentSearchTerm!: string | undefined;

  public searchForm: UntypedFormGroup = this.fb.group({
    searchTerm: [''],
    customers: [[]],
    customer: [''],
    customerCode: [''],
    statuses: [[]],
    flows: [[]],
    customerLaboratory: [[]],
  });

  public selectedTaskId = '';

  public taskStatuses: string[] = this.taskStatusIconMap
    .map((x) => x.filterName)
    .filter((v, i, a) => a.map((x) => x).indexOf(v) === i);

  public flows: string[] = Object.entries(ProcessFlow)
    .filter((f) => !isNaN(Number(f[0])))
    .map((e) => e[1]) as string[];

  public dataSource: MatTableDataSource<TaskBasicInfo> = new MatTableDataSource();
  public displayedColumns: string[] = [
    'status',
    'flow',
    'customerName',
    'createdOn',
  ];
  public autoRefresh = false;
  public autoRefreshAlertFlag = false;

  public customerNames: BehaviorSubject<string[]> = new BehaviorSubject(
    [] as string[]
  );
  public statuses: BehaviorSubject<StatusFilter[]> = new BehaviorSubject(
    [] as StatusFilter[]
  );

  private subs = new Array<Subscription>();
  private autoRefreshSubscription!: Subscription;
  private filterValues: TaskFilter = {
    inputSearch: '',
    customers: [],
    dateFrom: undefined,
    dateTo: undefined,
    isOrder: false,
    isResult: false,
    isInvoice: false,
    isAction: false,
    statuses: [],
  };

  public constructor(
    public accessService: RightsService,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private store: Store,
    private toastr: ToastrService,
  ) {}

  public ngOnInit(): void {
    this.setFormForSearch();

    this.subs.push(
      this.route.params.subscribe((params) => {
        this.applyParamsTaskFilters(params);
      })
    );
    this.store.dispatch(new FetchCurrentUserLaboratoriesBasicInfo());
    this.store.dispatch(new FetchCurrentUserAllCustomersBasicInfo());
    this.subs.push(
      this.arePluginsLoadedOrLoading$.subscribe((arePluginsLoadedOrLoading: boolean) => {
        if (!arePluginsLoadedOrLoading) {
          this.store.dispatch(new FetchAllPluginsBasicInfo());
        }
      })
    );

    if (!this.advancedSearchEnabled) {
      this.refresh(TasksRefreshMode.auto);
    }

    this.subs.push(
      this.tasks$.subscribe((tasks: TaskBasicInfo[]) => {
        void (async () => {
          this.customerNames.next([
            ...new Set(tasks.map((item) => item.customerName)),
          ]);
          this.statuses.next(
            [
              ...new Set(
                tasks.map((element) =>
                  this.getTaskStatusIconAndName(
                    element.requestedProcess,
                    element.state,
                  ),
                ),
              ),
            ]
              .map(
                (element) =>
                  new StatusFilter(
                    element.icon,
                    this.getTaskStatusSearchFilterFromIconAndState(element),
                  ),
              )
              .filter((v, i, a) => a.map((x) => x.name).indexOf(v.name) === i),
          );

          const extendedData = await this.parseTasks(tasks);
          extendedData.forEach(
            (element: TaskBasicInfo) =>
              (element.status = this.getTaskStatusIconAndName(
                element.requestedProcess,
                element.state,
              ).icon!),
          );

          if (tasks.length > 0) {
            this.dateFilter?.setDatesFromString(
              tasks[0].createdOn.toString(),
              tasks[tasks.length - 1].createdOn.toString(),
            );
          } else {
            this.dateFilter?.clearFilter();
          }
          this.dataSource.data = extendedData;
          this.dataSource.sortingDataAccessor = (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            data: any,
            sortHeaderId: string,
          ) => data[sortHeaderId]?.toLowerCase();
          this.dataSource.sort = this.sort;
          this.refreshDataSource();
        })();
      }),
    );

    this.subs.push(
      this.selectedTaskId$.subscribe((taskId: string) => {
        this.selectedTaskId = taskId;
      })
    );

    this.subs.push(
      this.customers$.subscribe((customers: CustomerBasicInfo[]) => {
        this.customersDictionary = customers
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
        this.filteredCustomersDictionary = customers
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
      })
    );

    this.subs.push(
      this.laboratories$.subscribe((laboratories: LaboratoryBasicInfo[]) => {
        this.laboratoriesDictionary = laboratories
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
        this.filteredLaboratoriesDictionary = laboratories
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  public ngAfterViewInit(): void {
    this.refreshDataSource();
    this.subs.push(fromEvent<KeyboardEvent>(this.searchInput.nativeElement, 'keyup')
      .pipe(
        map((e) => (e.target as HTMLInputElement).value),
        debounceTime(1000)
      )
      .subscribe(() => {
        if (!this.advancedSearchOpened) {
          this.resetFormWithoutSearch();
          this.refresh(TasksRefreshMode.auto);
        }
      }));

    this.setTimeIntervalFilter();

    if (this.advancedSearchEnabled) {
      this.refresh(TasksRefreshMode.auto);
    }
  }

  public ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.autoRefreshSubscription?.unsubscribe();
  }

  public applyFilters(): void {
    this.dataSource.filter = JSON.stringify(this.filterValues);
    this.advancedSearchEnabled = true;
    this.periodChangedFlag = true;
    this.refresh(TasksRefreshMode.manual);
    this.toggleAdvancedSearch();
  }

  public refresh(tasksRefreshMode: TasksRefreshMode): void {
    const searchForm = { ...this.searchForm.value } as TaskSearchForm;
    if (searchForm.searchTerm) {
      if(this.lastSentSearchTerm !== searchForm.searchTerm){
        this.autoRefreshAlertFlag = false;
      }
      this.lastSentSearchTerm = searchForm.searchTerm;
    }
    else {
      this.lastSentSearchTerm = '';
    }
    searchForm.customerLaboratory =
      this.searchForm.value.customerLaboratory?.join(' ');
    searchForm.customers = this.searchForm.value.customers?.join(' ');
    const tasksMap = this.taskStatusIconMap.filter((x) =>
      ((this.searchForm.value.statuses
        .map((element: string) =>
          element.toLowerCase()) as string[]) ?? [])
        .includes(x.filterName.toLowerCase())
    );

    const messageStatesMap = new Map<string, Set<string>>();

    tasksMap.forEach((element) => {
      const lowercaseFilterName = element.filterName.toLowerCase();
      const messageState = element.messageState;
    
      if (!messageStatesMap.has(lowercaseFilterName)) {
        messageStatesMap.set(lowercaseFilterName, new Set([messageState]));
      } else {
        messageStatesMap.get(lowercaseFilterName)?.add(messageState);
      }
    });

    const uniqueMessageStatesList: string[] = Array.from(messageStatesMap.values()).map((set) => Array.from(set).join(' '));
    searchForm.messageStates = uniqueMessageStatesList;

    searchForm.processFlows = this.searchForm.value.flows?.join(' ');

    const processTypesMap = new Map<string, Set<string>>();

    tasksMap.forEach((element) => {
      const lowercaseFilterName = element.filterName.toLowerCase();
      const processType = element.processType;

      if (!processTypesMap.has(lowercaseFilterName)) {
        processTypesMap.set(lowercaseFilterName, new Set([processType]));
      } else {
        processTypesMap.get(lowercaseFilterName)?.add(processType);
      }
    });
    const uniqueProcessTypesList: string[] = Array.from(processTypesMap.values()).map((set) => Array.from(set).join(' '));
    searchForm.processTypes = uniqueProcessTypesList;

    if (
      searchForm.searchTerm ||
      this.advancedSearchOpened ||
      this.advancedSearchEnabled
    ) {
      this.store
        .dispatch(
          new FetchTasksBasicInfo(
            this.dateRange.getFromDate(),
            this.dateRange.getToDate(),
            searchForm as TaskSearchForm,
          )
        )
        .pipe(first(), withLatestFrom(this.tasks$))
        .subscribe(([, tasks]) => {
          const errorMessage = this.store.selectSnapshot(TasksState.getErrorMessage);

          if(errorMessage){
            this.toastr.error(errorMessage);
            return;
          }

          if(tasksRefreshMode === TasksRefreshMode.manual || this.isFirstAutoRefresh()){
            this.displayTaskLimitAlertMessage(tasks);
          }
          
          this.dataSource.data = tasks; 
          const task = this.dataSource
            .sortData(this.dataSource.filteredData, this.sort)
            .find((task) => task.id === this.selectedTaskId) as TaskBasicInfo;
            
          this.setCurrentTaskSelectedTask(task);

          if(this.isFirstAutoRefresh()){
            this.autoRefreshAlertFlag = true;
          }
        });
    } else {
      this.advancedSearchEnabled = false;
      this.store.dispatch(
        new FetchTasksBasicInfo(
          format(new Date(), 'yyyy-MM-dd'),
          format(new Date(), 'yyyy-MM-dd'),
          searchForm as TaskSearchForm
        )
      );
    }
  }

  public selectRow(taskId: string): void {
    if (taskId !== this.selectedTaskId) {
      this.store.dispatch(new SetCurrentTaskId(taskId));
    }
  }

  public periodChanged(): void {
    this.periodChangedFlag = true;
  }

  public autoRefreshChanged(event: MatCheckboxChange): void {
    this.autoRefresh = event.checked;
    if (this.autoRefresh) {
      this.autoRefreshAlertFlag = false;
      this.runAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  public getTaskStatusIconAndName(
    processType: ProcessType,
    messageState: MessageState
  ): StatusFilter {
    const taskStatusIcon = this.taskStatusIconMap.find(
      (x) =>
        x.processType === ProcessType[processType] &&
        x.messageState === MessageState[messageState]
    )?.iconName;
    if (taskStatusIcon) {
      return new StatusFilter(taskStatusIcon, MessageState[messageState]);
    }

    return new StatusFilter('unknown', MessageState[messageState]);
  }

  public getTaskStatusIconTitle(
    processType: ProcessType,
    messageState: MessageState
  ): string {
    const taskStatusIcon = this.taskStatusIconMap.find(
      (x) =>
        x.processType === ProcessType[processType] &&
        x.messageState === MessageState[messageState]
    )?.filterName;
    return taskStatusIcon ?? 'unknown';
  }

  public getTaskStatusSearchFilterFromIconAndState(stateFilter: StatusFilter): string {
    return (
      this.taskStatusIconMap.find((x) => x.iconName === stateFilter.icon && 
        x.messageState === stateFilter.name)?.filterName ??
      'unknown'
    );
  }

  public getTaskStatusIconTitleFromIcon(icon: string): string {
    if (icon) {
      return icon.split('_').join(' ');
    }

    return 'unknown';
  }

  public applyCustomersFilter(customers: string[]): void {
    this.filterValues.customers = customers;
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  public applyDateTimeFilter(datetimeFilter: DateTimeRangeFilter): void {
    if (!datetimeFilter) {
      return;
    }

    this.filterValues.dateTo = datetimeFilter.to;
    this.filterValues.dateFrom = datetimeFilter.from;
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  public applyInputOutputFilter(filter: InputOutputFilter): void {
    if (!filter) {
      return;
    }

    this.filterValues.isOrder = filter.isOrder;
    this.filterValues.isResult = filter.isResult;
    this.filterValues.isInvoice = filter.isInvoice;
    this.filterValues.isAction = filter.isAction;
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  public applyStatusesFilter(statuses: string[]): void {
    let searchTerms: string[] = [];
    statuses.forEach(
      (x) =>
        (searchTerms = searchTerms.concat(
          this.taskStatusIconMap
            .filter((y) => y.filterName === x)
            .map((i) => i.iconName)
        ))
    );
    if (statuses.some((x) => x === 'unknown')) {
      searchTerms.push('unknown');
    }
    this.filterValues.statuses = searchTerms.filter(
      (v, i, a) => a.indexOf(v) === i
    );
    this.dataSource.filter = JSON.stringify(this.filterValues);
  }

  public isDataEmpty(): boolean {
    return (
      !this.dataSource?.data ||
      this.dataSource.data.length === 0
    );
  }

  public toggleAdvancedSearch(): void {
    this.advancedSearchOpened = !this.advancedSearchOpened;
  }

  public resetFormWithoutSearch(): void {
    this.clearSearchFormFields();
    this.dateRange.setInitialDateRange();
    this.periodChangedFlag = false;
    this.advancedSearchEnabled = false;
  }

  public resetSearch(): void {
    this.clearSearchFormFields();
    this.searchForm.get('searchTerm')?.setValue('');
    this.dateRange.setInitialDateRange();
    this.advancedSearchEnabled = false;
    this.advancedSearchOpened = false;
    this.periodChangedFlag = false;
    this.lastSentSearchTerm = undefined;
    this.statusFilter.clearFilters();
    this.flowFilter.clearFilters();
    this.customerFilter.clearFilters();
    this.refresh(TasksRefreshMode.auto);
  }

  public clearSearchFormFields(): void {
    this.searchForm.get('customer')?.setValue('');
    this.searchForm.get('customerCode')?.setValue('');
    this.searchForm.get('statuses')?.setValue([]);
    this.searchForm.get('flows')?.setValue([]);
    this.searchForm.get('customerLaboratory')?.setValue([]);
    this.searchForm.get('customers')?.setValue([]);
  }

  public displayedTextIfEmpty(): string {
    if (this.advancedSearchEnabled) {
      return 'extendedSearch';
    }

    if (this.lastSentSearchTerm) {
      return 'normalSearch';
    }

    return 'unknown';
  }

  public getCustomerName(id: string): string {
    const elements = this.customersDictionary?.filter((x) => x.id === id);
    return elements && elements.length > 0
      ? elements.slice().sort((a, b) => a.name.localeCompare(b.name))[0].name
      : '';
  }

  public getLaboratoryName(id: string): string {
    const elements = this.laboratoriesDictionary?.filter((x) => x.id === id);
    return elements && elements.length > 0
      ? elements.slice().sort((a, b) => a.name.localeCompare(b.name))[0].name
      : '';
  }

  public filterCustomers(query: string): void {
    if (!query || query.length < 3) {
      this.filteredCustomersDictionary = this.customersDictionary;
      return;
    }

    this.filteredCustomersDictionary = this.customersDictionary?.filter((x) =>
      x.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  public customersSelectToggled(): void {
    this.filteredCustomersDictionary = this.customersDictionary;
    this.customersSearch.nativeElement.value = '';
  }

  public isCustomerInFilter(customer: CustomerBasicInfo): boolean {
    return this.filteredCustomersDictionary.some((x) => x.id === customer.id);
  }

  public filterLaboratories(query: string): void {
    if (!query || query.length < 3) {
      this.filteredLaboratoriesDictionary = this.laboratoriesDictionary;
      return;
    }

    this.filteredLaboratoriesDictionary = this.laboratoriesDictionary?.filter(
      (x) => x.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  public laboratoriesSelectToggled(): void {
    this.filteredLaboratoriesDictionary = this.laboratoriesDictionary;
    this.laboratorySearch.nativeElement.value = '';
  }

  public isLaboratoryInFilter(laboratory: LaboratoryBasicInfo): boolean {
    return this.filteredLaboratoriesDictionary.some(
      (x) => x.id === laboratory.id
    );
  }

  public isSearchFormEmpty(): boolean {
    const searchForm = this.searchForm.value;
    return (
      !searchForm.searchTerm &&
      !searchForm.customerReference &&
      !searchForm.customerCode &&
      (!searchForm.customerLaboratory ||
        searchForm.customerLaboratory.length === 0) &&
      (!searchForm.statuses || searchForm.statuses.length === 0) &&
      (!searchForm.flows || searchForm.flows.length === 0) &&
      (!searchForm.customers || searchForm.customers.length === 0) &&
      this.dateRange.isInitialDateSet() &&
      !this.advancedSearchEnabled
    );
  }

  private applyParamsTaskFilters(params: Params): void {
    const customerId = params.customerId;
    if (customerId) {
      const selectedCustomerIds = [];
      selectedCustomerIds.push(customerId);
      this.searchForm.get('customers')?.setValue(selectedCustomerIds);
    }
    const laboratoryId = params.laboratoryId;
    if (laboratoryId) {
      const selectedLaboratoryIds = [];
      selectedLaboratoryIds.push(laboratoryId);
      this.searchForm.get('customerLaboratory')?.setValue(selectedLaboratoryIds);
    }
    const taskStatus = params.status;
    if (taskStatus) {
      const selectedtaskStatuses = [];
      selectedtaskStatuses.push(taskStatus);
      this.searchForm.get('statuses')?.setValue(selectedtaskStatuses);
    }

    if (customerId || laboratoryId || taskStatus) {
      this.searchForm.markAsDirty();
      this.advancedSearchEnabled = true;
    }
  }

  private isFirstAutoRefresh(): boolean {
    return this.autoRefresh === true && this.autoRefreshAlertFlag === false;
  }

  private displayTaskLimitAlertMessage(tasks: TaskBasicInfo[]): void {
    if ((tasks as TaskBasicInfo[]).length === 10000) {
      alert('Search was limited to 10000 tasks!');
    }
  }

  private setCurrentTaskSelectedTask(task: TaskBasicInfo): void {
    if (task) {
      this.store.dispatch(new SetCurrentTaskId(task.id));
      this.selectRow(task.id);
    }
  }
  
  private setTimeIntervalFilter() {
    this.route.params.subscribe((params) => {
      if(params.endDate && params.startDate){
        const endDate = new Date(params.endDate);
        const startDate = new Date(params.startDate);

        this.dateRange.setDateRange(endDate, startDate);
    }
    });
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async parseTasks(list: TaskBasicInfo[]): Promise<any> {
    return Promise.resolve(JSON.parse(JSON.stringify(list)));
  }

  private runAutoRefresh(): void {
    const reloadIntervalSeconds = 10;

    this.autoRefreshSubscription = timer(0, reloadIntervalSeconds * 1000)
      .pipe(mergeMap(async () => this.refresh(TasksRefreshMode.auto)))
      .subscribe();
  }

  private stopAutoRefresh(): void {
    this.autoRefreshSubscription.unsubscribe();
  }

  private refreshDataSource(): void {
    this.dataSource.paginator = this.paginator;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.dataSource.filterPredicate = (data: any, filter: string) => this.filterData(data, filter);
  }
  
  private setFormForSearch(): void {
    this.searchForm.get('searchTerm')?.setValue('');
    this.searchForm.get('customerReference')?.setValue('');
    this.searchForm.get('customerCode')?.setValue('');
    this.searchForm.get('customerLaboratory')?.setValue([]);
    this.searchForm.get('customers')?.setValue([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private filterData(data: any, filter: string): boolean {
    const searchTerms = JSON.parse(filter);
    const columnsToFilter = [
      'customerName',
      'customerReference',
      'createdOn',
      'processFlow',
      'status',
    ];
    let pluginHistory = data.pluginHistory;
    if (!pluginHistory) {
      pluginHistory = [];
    }
    let displayedColumnsData = '';
    columnsToFilter.forEach((column) => {
      displayedColumnsData += data[column];
    });

    const customerNamesCondition =
      searchTerms.customers.length === 0 ||
      searchTerms.customers.some(
        (x: string) =>
          displayedColumnsData.toLowerCase().indexOf(x.toLowerCase()) !== -1
      );

    const createDate = new Date(data[columnsToFilter[2]]);
    let createDateCondition = true;
    if (searchTerms.dateFrom && searchTerms.dateTo) {
      const filterDateFrom = new Date(searchTerms.dateFrom);
      const filterDateTo = new Date(searchTerms.dateTo);
      createDateCondition =
        createDate >= filterDateFrom && createDate <= filterDateTo;
    }

    const flow = data[columnsToFilter[3]];
    const flowCondition = this.isFlowConditionValid(searchTerms, flow);

    const status = data[columnsToFilter[4]];
    const statusCondition =
      searchTerms.statuses.length === 0 ||
      searchTerms.statuses.some(
        (x: string) => x.toLowerCase().indexOf(status.toLowerCase()) !== -1
      );

    return (
      customerNamesCondition &&
      createDateCondition &&
      flowCondition &&
      statusCondition
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isFlowConditionValid(searchTerms: any, flow: any): boolean {
    return (searchTerms.isOrder && flow === 0) ||
      (searchTerms.isResult && flow === 1) ||
      (searchTerms.isInvoice && flow === 2) ||
      (searchTerms.isAction && flow === 3) ||
      (!searchTerms.isOrder &&
        !searchTerms.isResult &&
        !searchTerms.isInvoice &&
        !searchTerms.isAction);
  }
}
